import React, { useRef } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../../../util/reactIntl';
import { isMainSearchTypeKeywords } from '../../../../util/search';

import {
  Form, LocationAutocompleteInput, KeywordAutocompleteInput, FieldTextInput,
} from '../../../../components';

import IconSearchDesktop from './IconSearchDesktop';
import css from './TopbarSearchForm.module.css';

const identity = v => v;

const KeywordSearchField = props => {
  const {
    keywordSearchWrapperClasses, desktopInputRootClass, iconClass, intl, isMobile = false, inputRef, onKeywordChange,
  } = props;

  return (
    <div className={keywordSearchWrapperClasses}>
      <Field
        name="keywords"
        render={({ input, meta }) => {
          const { onChange, ...restInput } = input;

          // Merge the standard onChange function with custom behaviur. A better solution would
          // be to use the FormSpy component from Final Form and pass onChange to the
          // onChange prop but that breaks due to insufficient subscription handling.
          // See: https://github.com/final-form/react-final-form/issues/159
          const searchOnChange = value => {
            onChange(value);
            onKeywordChange(value);
          };

          return (
            <KeywordAutocompleteInput
              className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
              iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
              inputClassName={isMobile ? css.mobileInput : css.desktopInput}
              predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
              iconCloseClassName={isMobile ? css.mobileCloseIcon : css.desktopIcon}
              predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
              placeholder={intl.formatMessage({ id: 'TopbarSearchForm.keywordPlaceholder' })}
              closeOnBlur={!isMobile}
              inputRef={inputRef}
              input={{ ...restInput, onChange: searchOnChange }}
              meta={meta}
            />
          );
        }}
      />
    </div>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile = false, inputRef, onLocationChange } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        // Merge the standard onChange function with custom behaviur. A better solution would
        // be to use the FormSpy component from Final Form and pass onChange to the
        // onChange prop but that breaks due to insufficient subscription handling.
        // See: https://github.com/final-form/react-final-form/issues/159
        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={isMobile ? css.mobileInput : css.desktopInput}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            iconCloseClassName={isMobile ? css.mobileCloseIcon : css.desktopIcon}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
            showDiscardIcon={true}
          />
        );
      }}
    />
  );
};

/**
 * The main search form for the Topbar.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.desktopInputRoot root class for desktop form input
 * @param {Function} props.onSubmit
 * @param {boolean} props.isMobile
 * @param {Object} props.appConfig
 * @returns {JSX.Element} search form element
 */
const TopbarSearchForm = props => {
  const searchInpuRef = useRef(null);
  const intl = useIntl();
  const { appConfig, onSubmit, searchType, ...restOfProps } = props;

  const onChange = location => {
    if (!isMainSearchTypeKeywords(appConfig) && location.selectedPlace) {
      // Note that we use `onSubmit` instead of the conventional
      // `handleSubmit` prop for submitting. We want to autosubmit
      // when a place is selected, and don't require any extra
      // validations for the form.
      onSubmit({ location });
      // blur search input to hide software keyboard
      searchInpuRef?.current?.blur();
    }
  };

  const onKeywordSubmit = values => {
//    if (isMainSearchTypeKeywords(appConfig)) {
      onSubmit({ keywords: values.keywords, searchType: values.searchType });
      // blur search input to hide software keyboard
      searchInpuRef?.current?.blur();
//    }
  };

  const isKeywordsSearch = searchType === 'keyword'; // isMainSearchTypeKeywords(appConfig);
  const submit = isKeywordsSearch ? onKeywordSubmit : onSubmit;
  return (
    <FinalForm
      {...restOfProps}
      onSubmit={submit}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          desktopInputRoot,
          isMobile = false,
          handleSubmit,
          formId,
          values,
        } = formRenderProps;
        const classes = classNames(rootClassName, className);
        const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;

        // Location search: allow form submit only when the place has changed
        const preventFormSubmit = e => e.preventDefault();
        const submitFormFn = isKeywordsSearch ? handleSubmit : preventFormSubmit;

        const keywordSearchWrapperClasses = classNames(
          css.keywordSearchWrapper,
          isMobile ? css.mobileInputRoot : desktopInputRootClass
        );

        return (
          <Form className={classes} onSubmit={submitFormFn} enforcePagePreloadFor="SearchPage">
            { searchType === 'location' ? (
              <LocationSearchField
                desktopInputRootClass={desktopInputRootClass}
                intl={intl}
                isMobile={isMobile}
                inputRef={searchInpuRef}
                onLocationChange={onChange}
              />
            ) : (
              <KeywordSearchField
                keywordSearchWrapperClasses={keywordSearchWrapperClasses}
                desktopInputRootClass={desktopInputRootClass}
                iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                intl={intl}
                isMobile={isMobile}
                inputRef={searchInpuRef}
                onKeywordChange={onChange}
              />
            )}
            <FieldTextInput
              id={`${formId}.searchType`}
              name="searchType"
              type="hidden"
            />
          </Form>
        );
      }}
    />
  );
};

export default TopbarSearchForm;
